import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getRunProgress, isRunActive } from "@/hooks/jobs/useAITopMatches";
import type { AITopMatchesRun } from "@/hooks/jobs/useAITopMatches";
import { AITopMatchesIcon } from "./AITopMatchesIcon";

interface AITopMatchesButtonProps {
	run: AITopMatchesRun | null;
	disabled?: boolean;
	onClick: () => void;
}

/** Entry point of AI Top 25: looks different when no search ran yet, when one is running and when its results are ready. */
export function AITopMatchesButton({ run, disabled, onClick }: AITopMatchesButtonProps) {
	const { t } = useTranslation();
	const running = isRunActive(run);
	const completed = run?.status === "completed";

	if (running && run) {
		const { overall } = getRunProgress(run);
		return (
			<Button
				onClick={onClick}
				disabled={disabled}
				variant="outline"
				className="relative flex items-center gap-1.5 overflow-hidden border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:text-purple-800 disabled:pointer-events-none disabled:opacity-50 dark:border-purple-800 dark:bg-purple-950/30 dark:text-purple-300"
			>
				<span className="absolute inset-y-0 left-0 bg-purple-200/70 transition-all duration-1000 ease-out dark:bg-purple-800/40" style={{ width: `${overall}%` }} />
				<AITopMatchesIcon working className="relative h-4 w-4" />
				<span className="relative">{t("aiTopMatches.buttonRunning", { percent: overall })}</span>
			</Button>
		);
	}

	return (
		<Button
			onClick={onClick}
			disabled={disabled}
			className={cn(
				"flex items-center gap-1.5 disabled:pointer-events-none disabled:opacity-50",
				completed ? "bg-emerald-600 hover:bg-emerald-700" : "bg-purple-600 hover:bg-purple-700",
			)}
		>
			<AITopMatchesIcon done={completed} className="h-4 w-4" />
			<span>{completed ? t("aiTopMatches.buttonDone") : t("aiTopMatches.button")}</span>
		</Button>
	);
}
